import React, { useState, useEffect } from 'react';
import './Dashboard.css';
import AccountInfo from './AccountInfo';
import AssetList from './AssetList';
import TransactionList from './TransactionList';
import MoUCard from './MoUCard';
import MoUDetailsPopup from '../MoUDetailsPopup';
import ErrorBoundary from '../ErrorBoundary';
import AddNetworkButton from '../common/AddNetworkButton';
import NetworkSelector from './NetworkSelector';
import { useWallet } from '../../hooks/useWallet';
import { useTokens } from '../../hooks/useTokens';
import { useTransactions } from '../../hooks/useTransactions';
import { fetchAssets, getCurrentGasPrice } from '../../services/assetService';
import { getLatestBlockNumber } from '../../services/earthApiService';
import { createMoU, getMoUs } from '../../services/mouService';

function Dashboard() {
  const { isConnected, account, connectWallet, disconnectWallet, switchToEarthNetwork } = useWallet();
  const { tokens, isLoading: tokensLoading } = useTokens(account);
  const { transactions, isLoading: transactionsLoading, error: transactionsError } = useTransactions(account);
  const [counterpartyAddress, setCounterpartyAddress] = useState('');
  const [networkStatus, setNetworkStatus] = useState('Disconnected');
  const [assets, setAssets] = useState([]);
  const [blockNumber, setBlockNumber] = useState('Loading...');
  const [gasPrice, setGasPrice] = useState('Loading...');
  const [error, setError] = useState(null);
  const [mouCreationStatus, setMouCreationStatus] = useState(null);
  const [selectedMouId, setSelectedMouId] = useState(null);
  const [initiatorName, setInitiatorName] = useState('');
  const [counterpartyName, setCounterpartyName] = useState('');
  const [mous, setMous] = useState([]);
  const [networkInfo, setNetworkInfo] = useState({});

  useEffect(() => {
    console.log('Transactions:', transactions);
  }, [transactions]);

  useEffect(() => {
    console.log('Current account:', account);
  }, [account]);

  useEffect(() => {
    if (isConnected) {
      switchToEarthNetwork();
      setNetworkStatus('Connected to Earth Network');
    } else {
      setNetworkStatus('Disconnected');
    }
  }, [isConnected, switchToEarthNetwork]);

  useEffect(() => {
    if (account) {
      fetchMoUs();
    }
  }, [account]);

  const fetchMoUs = async () => {
    try {
      const fetchedMous = await getMoUs(account);
      setMous(fetchedMous);
    } catch (error) {
      console.error('Error fetching MoUs:', error);
    }
  };

  const handleMoUUpdate = async () => {
    await fetchMoUs();
  };

  const handleViewMoUDetails = (mouId) => {
    setSelectedMouId(mouId);
  };

  useEffect(() => {
    async function fetchData() {
      if (account) {
        try {
          const fetchedAssets = await fetchAssets(account);
          setAssets(fetchedAssets);

          const latestBlockNumber = await getLatestBlockNumber();
          setBlockNumber(latestBlockNumber);

          const currentGasPrice = await getCurrentGasPrice();
          setGasPrice(currentGasPrice);

          setError(null);
        } catch (err) {
          console.error('Error fetching dashboard data:', err);
          setError('Failed to fetch some dashboard data. Some information may be unavailable.');
        }
      }
    }
    fetchData();
  }, [account]);

  const handleCreateMoU = async () => {
    if (!account || !counterpartyAddress || !initiatorName || !counterpartyName) {
      setMouCreationStatus('Please connect your wallet, enter a valid counterparty address, and provide both names.');
      return;
    }

    try {
      console.log("Creating MoU with account:", account);
      const result = await createMoU(account, counterpartyAddress, initiatorName, counterpartyName);
      if (result.success) {
        setMouCreationStatus(`MoU creation initiated. Transaction hash: ${result.transactionHash}`);
      } else {
        setMouCreationStatus(`Failed to create MoU: ${result.error}`);
      }
    } catch (error) {
      console.error('Error creating MoU:', error);
      setMouCreationStatus('An unexpected error occurred while creating the MoU. Please check your wallet connection and try again.');
    }
  };

  const getNetworkName = (chainId) => {
    switch (chainId) {
      case '0x1':
        return 'Ethereum Mainnet';
      case '0xaa36a7':
        return 'Sepolia Testnet';
      default:
        return 'Unknown Network';
    }
  };

  const getNativeCurrency = (chainId) => {
    switch (chainId) {
      case '0x1':
        return 'ETH';
      case '0xaa36a7':
        return 'SepoliaETH';
      default:
        return 'Unknown';
    }
  };

  useEffect(() => {
    const fetchNetworkInfo = async () => {
      if (window.ethereum && window.ethereum.request) {
        try {
          const chainId = await window.ethereum.request({ method: 'eth_chainId' });
          const networkName = getNetworkName(chainId);
          const latestBlockNumber = await getLatestBlockNumber();
          const currentGasPrice = await getCurrentGasPrice();

          setNetworkInfo({
            chainId,
            networkName,
            nativeCurrency: getNativeCurrency(chainId),
            latestBlock: latestBlockNumber,
            currentGasPrice,
          });
        } catch (error) {
          console.error("Error fetching network information:", error);
        }
      }
    };

    fetchNetworkInfo();
  }, [isConnected]);

  if (error) {
    return <ErrorBoundary error={error} />;
  }

  return (
    <div className="dashboard">
      <header className="header">
        <div className="logo-title">
          <img src={`${process.env.PUBLIC_URL}/human.svg`} alt="Earth Logo" className="earth-logo" />
          <h1>Earth</h1>
        </div>
        <div className="header-controls">
          <NetworkSelector />
          <div className="button-group">
            {isConnected ? (
              <button onClick={disconnectWallet} className="disconnect-button">Disconnect</button>
            ) : (
              <button onClick={connectWallet} className="connect-button">Connect</button>
            )}
            <AddNetworkButton />
          </div>
        </div>
      </header>
      <main className="main-content">
        {isConnected ? (
          <>
            <AccountInfo account={account} />
            <AssetList tokens={tokens} isLoading={tokensLoading} />
            <TransactionList 
              transactions={transactions} 
              isLoading={transactionsLoading} 
              error={transactionsError}
              account={account}
            />
            <MoUCard account={account} onViewMoUDetails={handleViewMoUDetails} />
          </>
        ) : (
          <p>Please connect your wallet to view the dashboard.</p>
        )}
      </main>
      {selectedMouId && (
        <MoUDetailsPopup
          mouId={selectedMouId}
          account={account}
          onClose={() => setSelectedMouId(null)}
          onUpdate={handleMoUUpdate}
        />
      )}
      <div className="network-info-container">
        <span className="network-info-title">Connected Network Information</span>
        <div className="info-button" title="Network Details">
          i
          <div className="info-tooltip">
            {networkInfo.chainId && <p>Chain ID: {networkInfo.chainId}</p>}
            {networkInfo.networkName && <p>Network: {networkInfo.networkName}</p>}
            {networkInfo.nativeCurrency && <p>Native Currency: {networkInfo.nativeCurrency}</p>}
            {networkInfo.latestBlock && <p>Latest Block: {networkInfo.latestBlock}</p>}
            {networkInfo.currentGasPrice && <p>Current Gas Price: {networkInfo.currentGasPrice}</p>}
          </div>
        </div>
      </div>
    </div>
  );
}

export default Dashboard;
